/**
 * Hit-testing opt-out for parts the viewer is currently hiding.
 *
 * Hiding a part type sets `visible={false}` on its group rather than
 * unmounting the renderer, so toggling a type back on does not re-download and
 * re-clone its meshes. Three's raycaster, however, never looks at `visible`
 * (`Raycaster.intersect()` only tests layers), and react-three-fiber does not
 * filter by visibility either - so a hidden part keeps taking part in hit
 * testing and, being solid geometry, shadows whatever sits behind it.
 *
 * That is how the hidden interior volume box - whose faces sit *inside* the
 * panels - swallowed the pointer over the recessed part of a butterfly lock:
 * through the lock's panel cutout the box was the nearest hit, and the static
 * part branch stops propagation, so the lock got no hover and no drag.
 */

/**
 * Skips the object *and its children*.
 *
 * Returning `false` is what makes three stop descending into the subtree
 * (`intersect()` sets `propagate = false` only for an exact `false`); the
 * `() => null` form used for single helper meshes would still let every child
 * mesh be tested.
 */
export const skipSubtreeRaycast = () => false as const;

/** Object3D's own no-op raycast, i.e. "test my children normally". */
export const defaultGroupRaycast = () => undefined;

/**
 * The `raycast` prop for a part wrapper.
 *
 * Always returns a function: react-three-fiber's prop diffing skips keys whose
 * value is `undefined`, so handing back `undefined` when a part becomes
 * visible again would leave the skip function in place forever.
 */
export const resolvePartRaycast = (isHidden: boolean) =>
  isHidden ? skipSubtreeRaycast : defaultGroupRaycast;
